/**
 * Accueil — the back office's home page.
 *
 * `/` used to be a redirect to the first screen the principal could reach,
 * which made the first screen depend on the order of a menu. It is now a
 * page with the three things a desk opens the application for:
 *
 * - **a search box** that lands on Catalogue › Articles with the term
 *   typed — "I look for an article" is the first question of the day;
 * - **the working context**, in one line under it, with the button that
 *   changes it;
 * - **the five spaces**, each with the screens this principal may open.
 *
 * An anonymous visitor is still sent to sign-in: `safeReturnTo` falls back
 * to `/`, and a sign-in with no `returnTo` must not land on a page that is
 * only a refusal.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAccess, useCan } from "@hardauto/core-access";
import { arrangeNavigation, navigationFor, type RouteDefinition } from "@hardauto/core-router";
import { Button, Card, Input, LoadingState, Page } from "@hardauto/core-ui";
import { CATALOG_PATHS } from "@hardauto/feature-catalog";
import { IAM_PATHS, useSession } from "@hardauto/feature-iam";

import { NAV_AREAS } from "./nav-areas";
import { routeTable } from "./routes";
import { useWorkspace } from "./workspace-context";
import "./i18n";

/** How many screens each space lists on the home page; the rail has the rest. */
const SHORTCUTS = 5;

export function HomeScreen() {
  const { t } = useTranslation(["backoffice", "errors"]);
  const navigate = useNavigate();
  const session = useSession();
  const { snapshot, ready } = useAccess();
  const mayConsult = useCan("catalog.enrichment.read");
  const { setPanelOpen } = useWorkspace();
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (session.status === "restoring") return;
    if (session.status !== "authenticated") {
      // `replace`, so the back button does not bounce between `/` and sign-in.
      void navigate({ to: IAM_PATHS.signIn, replace: true });
    }
  }, [navigate, session.status]);

  if (session.status !== "authenticated" || !ready) {
    return <LoadingState label={t("errors:loading.restoring")} />;
  }

  const permitted = (route: RouteDefinition): boolean => {
    const required = route.requirement?.permissions ?? [];
    if (required.length === 0) return true;
    return route.requirement?.mode === "any"
      ? snapshot.canAnyOf(required)
      : snapshot.canAllOf(required);
  };
  const entries = [...navigationFor(routeTable, "main"), ...navigationFor(routeTable, "admin")]
    .filter(permitted)
    .filter((route) => route.path !== "/");
  const spaces = arrangeNavigation(entries, NAV_AREAS).flatMap((node) =>
    node.kind === "area" ? [node] : [],
  );

  const name = session.user?.display_name ?? session.user?.email ?? "";

  return (
    <Page title={t("backoffice:home.title")} description={t("backoffice:home.greeting", { name })}>
      {/* The first question of the day, given the width of the page: one
          field, one button, and the way into the catalogue beside them. The
          working context sits under it in one line, because it decides what
          the search will answer — and the header already spells it out. */}
      <section className="hardauto-home__hero" aria-labelledby="hardauto-home-search">
        {mayConsult ? (
          <>
            <div className="hardauto-home__hero-head">
              <h2 id="hardauto-home-search" className="hardauto-home__hero-title">
                {t("backoffice:home.searchLabel")}
              </h2>
              <p className="hardauto-home__hero-hint">{t("backoffice:home.searchHint")}</p>
            </div>
            <form
              className="hardauto-home__search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                void navigate({
                  to: CATALOG_PATHS.consultation,
                  search: search.trim() ? { q: search.trim() } : {},
                });
              }}
            >
              <Input
                type="search"
                aria-label={t("backoffice:home.searchLabel")}
                placeholder={t("backoffice:home.searchPlaceholder")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Button type="submit" variant="primary">
                {t("backoffice:home.searchAction")}
              </Button>
              <Link to={CATALOG_PATHS.consultation} className="hardauto-home__browse">
                {t("backoffice:home.browse")}
              </Link>
            </form>
          </>
        ) : (
          <h2 id="hardauto-home-search" className="hardauto-visually-hidden">
            {t("backoffice:home.contextTitle")}
          </h2>
        )}
        <div className="hardauto-home__context">
          <span>
            <strong>{t("backoffice:home.contextTitle")}</strong> ·{" "}
            {t("backoffice:home.contextBody")}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPanelOpen(true);
            }}
          >
            {t("backoffice:context.change")}
          </Button>
        </div>
      </section>

      <div className="hardauto-home__grid">
        {spaces.map((space) => (
          <Card key={space.area.id} title={t(space.area.labelKey)} compactTitle flush>
            <ul className="hardauto-home__links">
              {space.entries.slice(0, SHORTCUTS).map((route) => (
                <li key={route.path}>
                  <Link to={route.path}>{t(route.nav.labelKey)}</Link>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </Page>
  );
}
